/* Dashboard Builder — full-screen editor for a single named board (preset).
 *
 * Create a new board or edit an existing one: compose a grid of tiles, pick
 * chart variants + colours, name it, and save as a shared tenant board
 * (admins) or a personal board (anyone). "Save as copy" forks the board into
 * a personal copy. New boards start seeded with the current default tiles. */
import React, { useMemo, useState } from 'react';
import GridLayout, { WidthProvider, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { X, Plus, Save, Copy, Trash2, GripVertical, Palette, SlidersHorizontal } from 'lucide-react';
import { DashboardDataProvider } from './DashboardData';
import { TileView, TILE_CATALOG, catalogByKey } from './tiles';
import {
  BandColors, DEFAULT_COLORS, DashPage, LayoutScope, Preset, TileInstance, CustomQuery,
} from './types';
import { createPreset, updatePreset, deletePreset } from './useLayouts';
import { DEFAULT_SEED, materialize } from './presets';
import { CustomChartConfig, defaultQuery } from './CustomChartConfig';

const Grid = WidthProvider(GridLayout);

const uid = (type: string) => `${type}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const COLOR_FIELDS: Array<{ key: keyof BandColors; label: string }> = [
  { key: 'Low', label: 'Low' }, { key: 'Medium', label: 'Medium' },
  { key: 'High', label: 'High' }, { key: 'Critical', label: 'Critical' },
  { key: 'accent', label: 'Accent' },
];

interface Props {
  page: DashPage;
  preset: Preset | null;          // null = create a new board
  canEditShared: boolean;
  onClose: () => void;
  onSaved: (savedId: string | null) => void;
}

export const BuilderOverlay: React.FC<Props> = ({ page, preset, canEditShared, onClose, onSaved }) => {
  const isNew = !preset;
  const [name, setName] = useState<string>(preset?.name || (page === 'analytics' ? 'My analytics board' : 'My dashboard'));
  const [tiles, setTiles] = useState<TileInstance[]>(
    preset?.tiles && preset.tiles.length ? [...preset.tiles] : materialize(DEFAULT_SEED[page]),
  );
  const [colors, setColors] = useState<BandColors>({ ...DEFAULT_COLORS, ...(preset?.colors || {}) });
  const [scope, setScope] = useState<LayoutScope>(preset?.scope || (canEditShared ? 'shared' : 'personal'));
  const [addOpen, setAddOpen] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [busy, setBusy] = useState(false);

  const [pick, setPick] = useState<string>(TILE_CATALOG[0].key);
  const pickEntry = catalogByKey(pick);
  const [pickChart, setPickChart] = useState<string>('');
  const [pickMetric, setPickMetric] = useState<string>('');
  const [pickTitle, setPickTitle] = useState<string>('');
  const [pickQuery, setPickQuery] = useState<CustomQuery>(defaultQuery());
  // Editing an existing custom tile's query.
  const [editTileId, setEditTileId] = useState<string | null>(null);
  const [editQuery, setEditQuery] = useState<CustomQuery>(defaultQuery());

  const gl: Layout[] = useMemo(() => tiles.map((t) => ({ i: t.i, x: t.x, y: t.y, w: t.w, h: t.h })), [tiles]);

  const onLayoutChange = (next: Layout[]): void => {
    setTiles((prev) => prev.map((t) => {
      const l = next.find((n) => n.i === t.i);
      return l ? { ...t, x: l.x, y: l.y, w: l.w, h: l.h } : t;
    }));
  };

  const openAdd = (): void => {
    const first = TILE_CATALOG[0];
    setPick(first.key);
    setPickChart(first.charts?.[0] || '');
    setPickMetric(first.metrics?.[0]?.key || '');
    setPickTitle(first.label);
    setPickQuery(defaultQuery());
    setAddOpen(true);
  };
  const onPickChange = (key: string): void => {
    const e = catalogByKey(key);
    setPick(key);
    setPickChart(e?.charts?.[0] || '');
    setPickMetric(e?.metrics?.[0]?.key || '');
    setPickTitle(e?.label || '');
    if (e?.custom) setPickQuery(defaultQuery());
  };
  const addTile = (): void => {
    const e = catalogByKey(pick);
    if (!e) return;
    const maxY = tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0);
    const metricLabel = e.metrics?.find((m) => m.key === pickMetric)?.label;
    const t: TileInstance = {
      i: uid(e.key), type: e.key,
      title: pickTitle || metricLabel || e.label,
      chart: (pickChart || undefined) as TileInstance['chart'],
      metric: e.metrics ? pickMetric : undefined,
      query: e.custom ? pickQuery : undefined,
      x: 0, y: maxY, w: e.defaultSize.w, h: e.defaultSize.h,
    };
    setTiles((prev) => [...prev, t]);
    setAddOpen(false);
  };
  const openEditQuery = (t: TileInstance): void => {
    setEditTileId(t.i);
    setEditQuery(t.query || defaultQuery());
  };
  const saveEditQuery = (): void => {
    setTiles((prev) => prev.map((t) => (t.i === editTileId ? { ...t, query: editQuery } : t)));
    setEditTileId(null);
  };
  const removeTile = (i: string): void => setTiles((prev) => prev.filter((t) => t.i !== i));
  const setTileChart = (i: string, chart: string): void =>
    setTiles((prev) => prev.map((t) => (t.i === i ? { ...t, chart: chart as TileInstance['chart'] } : t)));
  const setTileTitle = (i: string, title: string): void =>
    setTiles((prev) => prev.map((t) => (t.i === i ? { ...t, title } : t)));

  const errIf = (err: unknown, fallback: string): void => {
    const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    toast.error(detail || fallback);
  };

  const doSave = async (): Promise<void> => {
    if (!name.trim()) { toast.error('Give the board a name.'); return; }
    if (tiles.length === 0) { toast.error('Add at least one tile.'); return; }
    setBusy(true);
    try {
      let saved: Preset;
      if (isNew) saved = await createPreset(page, scope, name.trim(), tiles, colors);
      else saved = await updatePreset(preset!.id, name.trim(), tiles, colors);
      toast.success(`Saved "${saved.name}".`);
      onSaved(saved.id);
      onClose();
    } catch (err) { errIf(err, 'Could not save board.'); }
    finally { setBusy(false); }
  };

  const doSaveCopy = async (): Promise<void> => {
    if (tiles.length === 0) { toast.error('Add at least one tile.'); return; }
    const copyScope: LayoutScope = canEditShared ? scope : 'personal';
    setBusy(true);
    try {
      const saved = await createPreset(page, copyScope, `${name.trim() || 'Board'} copy`, tiles, colors);
      toast.success(`Saved copy "${saved.name}".`);
      onSaved(saved.id);
      onClose();
    } catch (err) { errIf(err, 'Could not copy board.'); }
    finally { setBusy(false); }
  };

  const doDelete = async (): Promise<void> => {
    if (!preset) return;
    if (!window.confirm(`Delete the board "${preset.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await deletePreset(preset.id);
      toast.success('Board deleted.');
      onSaved(null);
      onClose();
    } catch (err) { errIf(err, 'Could not delete board.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-raptor-neutral/95 backdrop-blur-sm flex flex-col" data-testid="dashboard-builder-overlay">
      {/* Toolbar */}
      <div className="shrink-0 bg-white border-b border-border/60 px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="font-headline text-lg text-raptor-navy mr-1">{isNew ? 'New' : 'Edit'} {page === 'analytics' ? 'analytics' : 'dashboard'} board</div>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Board name" className="w-56 h-9" data-testid="builder-name" />
        {isNew ? (
          <Select value={scope} onChange={(e) => setScope(e.target.value as LayoutScope)} className="w-44 h-9" data-testid="builder-scope">
            {canEditShared && <option value="shared">Shared (everyone)</option>}
            <option value="personal">Personal (only me)</option>
          </Select>
        ) : (
          <span className="text-xs px-2 py-1 rounded bg-raptor-neutral/50 text-raptor-navy/70" data-testid="builder-scope-label">
            {scope === 'shared' ? 'Shared board' : 'Personal board'}
          </span>
        )}
        <Button size="sm" variant="default" onClick={openAdd} data-testid="builder-add-tile"><Plus size={14} /> Add tile</Button>
        <Button size="sm" variant="outline" onClick={() => setShowColors((s) => !s)} data-testid="builder-toggle-colors"><Palette size={14} /> Colours</Button>
        <div className="ml-auto flex items-center gap-2">
          {!isNew && preset?.can_edit && (
            <Button size="sm" variant="outline" onClick={() => void doDelete()} disabled={busy} data-testid="builder-delete"><Trash2 size={14} /> Delete</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => void doSaveCopy()} disabled={busy} data-testid="builder-save-copy"><Copy size={14} /> Save as copy</Button>
          <Button size="sm" variant="default" onClick={() => void doSave()} disabled={busy} data-testid="builder-save"><Save size={14} /> Save</Button>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="builder-close"><X size={16} /></Button>
        </div>
      </div>

      {/* Colour palette editor */}
      {showColors && (
        <div className="shrink-0 bg-white border-b border-border/60 px-4 py-2 flex items-center gap-4 flex-wrap" data-testid="builder-color-editor">
          {COLOR_FIELDS.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-xs text-raptor-navy/80">
              {f.label}
              <input type="color" value={colors[f.key]} onChange={(e) => setColors((c) => ({ ...c, [f.key]: e.target.value }))}
                     className="w-7 h-7 rounded border border-border/60 cursor-pointer" data-testid={`builder-color-${f.key}`} />
            </label>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setColors({ ...DEFAULT_COLORS })} data-testid="builder-color-reset">Reset colours</Button>
        </div>
      )}

      {/* Canvas */}
      <div className="flex-1 overflow-auto p-4">
        {tiles.length === 0 ? (
          <div className="h-full grid place-items-center text-center" data-testid="builder-empty">
            <div>
              <div className="text-raptor-navy/60 font-body mb-3">This board is empty. Add tiles to compose it.</div>
              <Button variant="default" onClick={openAdd}><Plus size={14} /> Add a tile</Button>
            </div>
          </div>
        ) : (
          <DashboardDataProvider colors={colors}>
            <Grid className="layout" layout={gl} cols={12} rowHeight={80} margin={[16, 16]}
                  draggableHandle=".tile-drag" onLayoutChange={onLayoutChange} compactType="vertical">
              {tiles.map((t) => {
                const entry = catalogByKey(t.type);
                return (
                  <div key={t.i} className="rounded-lg border border-raptor-blue/40 bg-white flex flex-col overflow-hidden shadow-sm">
                    <div className="px-2 py-1 border-b border-border/50 flex items-center gap-1.5 shrink-0 bg-raptor-neutral/30">
                      <span className="tile-drag cursor-move text-raptor-navy/40 hover:text-raptor-navy"><GripVertical size={14} /></span>
                      <input value={t.title} onChange={(e) => setTileTitle(t.i, e.target.value)}
                             className="flex-1 min-w-0 bg-transparent text-xs font-semibold text-raptor-navy outline-none"
                             data-testid={`tile-title-${t.type}`} />
                      {entry?.charts && entry.charts.length > 1 && (
                        <select value={t.chart || entry.charts[0]} onChange={(e) => setTileChart(t.i, e.target.value)}
                                className="text-[10px] border border-border/60 rounded px-1 py-0.5 bg-white" data-testid={`tile-chart-${t.type}`}>
                          {entry.charts.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                      {t.type === 'custom' && (
                        <button type="button" onClick={() => openEditQuery(t)} className="text-raptor-navy/40 hover:text-raptor-blue" data-testid="tile-configure-custom"><SlidersHorizontal size={13} /></button>
                      )}
                      <button type="button" onClick={() => removeTile(t.i)} className="text-raptor-navy/40 hover:text-red-600" data-testid={`tile-remove-${t.type}`}><Trash2 size={13} /></button>
                    </div>
                    <div className="flex-1 min-h-0"><TileView tile={t} colors={colors} /></div>
                  </div>
                );
              })}
            </Grid>
          </DashboardDataProvider>
        )}
      </div>

      {/* Add-tile wizard */}
      {addOpen && (
        <div className="fixed inset-0 z-[2100] bg-black/40 grid place-items-center p-4" onClick={() => setAddOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="builder-add-wizard">
            <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between shrink-0">
              <div className="font-headline text-raptor-navy">Add a tile</div>
              <button type="button" onClick={() => setAddOpen(false)}><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-auto flex-1 min-h-0">
              <div className="space-y-1.5">
                <Label>Data source</Label>
                <Select value={pick} onChange={(e) => onPickChange(e.target.value)} data-testid="wizard-source">
                  {TILE_CATALOG.map((c) => <option key={c.key} value={c.key}>{`${c.group} · ${c.label}`}</option>)}
                </Select>
                {pickEntry && <p className="text-[11px] text-raptor-navy/60 font-body">{pickEntry.description}</p>}
              </div>
              {pickEntry?.custom ? (
                <CustomChartConfig value={pickQuery} onChange={setPickQuery} />
              ) : (
                <>
                  {pickEntry?.metrics && (
                    <div className="space-y-1.5">
                      <Label>Metric</Label>
                      <Select value={pickMetric} onChange={(e) => setPickMetric(e.target.value)} data-testid="wizard-metric">
                        {pickEntry.metrics.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                      </Select>
                    </div>
                  )}
                  {pickEntry?.charts && pickEntry.charts.length > 1 && (
                    <div className="space-y-1.5">
                      <Label>Chart type</Label>
                      <Select value={pickChart} onChange={(e) => setPickChart(e.target.value)} data-testid="wizard-chart">
                        {pickEntry.charts.map((c) => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </div>
                  )}
                </>
              )}
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={pickTitle} onChange={(e) => setPickTitle(e.target.value)} data-testid="wizard-title" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border/60 flex justify-end gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button variant="default" size="sm" onClick={addTile} data-testid="wizard-add"><Plus size={14} /> Add tile</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit custom-chart query */}
      {editTileId && (
        <div className="fixed inset-0 z-[2100] bg-black/40 grid place-items-center p-4" onClick={() => setEditTileId(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="custom-edit-modal">
            <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between shrink-0">
              <div className="font-headline text-raptor-navy">Configure chart</div>
              <button type="button" onClick={() => setEditTileId(null)}><X size={16} /></button>
            </div>
            <div className="p-4 overflow-auto flex-1 min-h-0"><CustomChartConfig value={editQuery} onChange={setEditQuery} /></div>
            <div className="px-4 py-3 border-t border-border/60 flex justify-end gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setEditTileId(null)}>Cancel</Button>
              <Button variant="default" size="sm" onClick={saveEditQuery} data-testid="custom-edit-save"><Save size={14} /> Apply</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
